import React, { useEffect, useLayoutEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { differenceInMinutes, isBefore, isEqual } from 'date-fns';
import { format } from 'date-fns';
import { DayShift } from '@/types';
import { MoreVertical, User, Check, Clock, AlertCircle, MoveHorizontal, Trash2, Move, Users, GripVertical, Zap, Lock, AlertTriangle } from 'lucide-react';
import { useHistoricalEdit } from '@/context/HistoricalEditContext';
import { RelativeBreak } from './ShiftTemplateItem';
import { addMinutes } from 'date-fns';
import { Employee } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { toast } from "@/hooks/ui/use-toast";
import { useEmployeeAvailability } from '@/hooks/employees/useEmployeeAvailability';
import { UnifiedEmployeeSelect } from '@/components/ui/unified-employee-select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Save } from 'lucide-react';
import { getZIndex, getZIndexClass } from '@/lib/scheduling/z-index-layers';
import { EDITING_COLORS } from '@/lib/scheduling/editing-colors';
import { ShiftStatus } from '@/lib/scheduling/shift-status-types';
import { getShiftStatusStyles, StyleVariant } from '@/lib/scheduling/shift-status-styles';
import { ShiftStatusBadge } from '@/components/shifts/status/ShiftStatusBadge';
import { useDimming } from '@/hooks/ui/useDimming';
import { ShiftTooltip } from '@/components/shifts/ShiftTooltip';
import { ShiftZoneControl } from '@/components/shifts/ShiftZoneControl';
import { EmployeeIndicator } from '@/components/shifts/EmployeeIndicator';
import { useEmployeeDragDrop } from '@/context/EmployeeSelectionContext';
import { useShiftUI } from '@/components/shifts/context/ShiftUIProvider';
import { useDashboardTab } from '@/context/DashboardTabContext';
import { useShiftResize } from '@/hooks/scheduling/useShiftResize';
// Visual state types and helper
type VisualState = 'default' | 'dragging' | 'drop-target' | 'hovered';

import { INTERVAL_MINUTES, ROW_HEIGHT_PX, GAP_HEIGHT_PX } from './constants';
import { enhancedShiftPositioningService } from '@/lib/scheduling/positioning-compatibility-layer';

// Module-level tracker for recently dropped shifts (survives component re-mounts)
const recentlyDroppedShifts = new Set<string>();

export function markShiftAsDropped(shiftId: string) {
  recentlyDroppedShifts.add(shiftId);
  // Auto-cleanup after animation window
  setTimeout(() => {
    recentlyDroppedShifts.delete(shiftId);
  }, 500);
}

export function isShiftRecentlyDropped(shiftId: string): boolean {
  return recentlyDroppedShifts.has(shiftId);
}

// Unified Draggable Shift Component
interface DraggableShiftProps {
  shift: DayShift;
  // Pixel positioning props (required for overlay positioning)
  left?: number; 
  width?: number;
  top?: number;
  position?: {
    left: number;
    width: number;
    top: number;
    height: number;
  };
  // Display and positioning context (required for position calculation)
  displayStartTime?: Date;
  displayEndTime?: Date;
  totalDisplayHours?: number;
  hourCellWidthPx?: number;
  hideDayColumn?: boolean;
  rowIndex?: number; // Day-local row index (0-based position within day)
  // Event handlers
  onClick?: (e: React.MouseEvent) => void; // Made optional for virtualized usage
  onEdit?: (shift: DayShift) => void;
  onDelete?: (shiftId: string, isNew?: boolean) => void;
  onReposition?: (shiftId: string, newStartTime: Date, newEndTime: Date) => void;
  // Optimistic positioning for smooth drag feedback
  onOptimisticPositionUpdate?: (shiftId: string, newStartTime: Date, newEndTime: Date) => void;
  onOptimisticPositionClear?: (shiftId: string) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  // Employee management
  employees: Employee[];
  onAssignEmployee?: (shiftId: string, employeeId: string | null) => void; // Made optional
  onEmployeeUpdate?: (shiftId: string, employeeId: string | null) => Promise<void>; // Alternative API
  // Additional context for optimized usage
  businessId?: string | null;
  selectedDate?: Date | null;
  // Feature flags
  isDraggableInPanel?: boolean;
  showMenu?: boolean;
  isHistorical?: boolean;
  calendarVariant?: 'full' | 'insights' | 'timetracking';
  // Data
  breaks?: RelativeBreak[];
  actualArrivalTime?: string | null;
  actualDepartureTime?: string | null;
  allDayShifts?: DayShift[];
  scrollContainerRef?: React.RefObject<HTMLElement>;
  // Shift selection for template editing
  onShiftSelect?: (shiftId: string, shouldSelect?: boolean) => void;
  selectedShiftIds?: Set<string>;
  actionsPanelMode?: string;
  // Actions panel control for standalone functionality
  onOpenActionsPanel?: () => void;
  onSetActionsPanelMode?: (mode: string) => void;
  // Drag handle control
  isActionsPanelOpen?: boolean;
}

// Update the component parameters and style
const DraggableShift: React.FC<DraggableShiftProps> = ({
  shift,
  // Pixel positioning props
  left: propLeft,
  width: propWidth,
  top: propTop = 0,
  position,
  // Display and positioning context
  displayStartTime,
  displayEndTime,
  totalDisplayHours,
  hourCellWidthPx,
  hideDayColumn,
  rowIndex,
  // Event handlers
  onClick,
  onEdit,
  onDelete,
  onReposition,
  // onOptimisticPositionUpdate - no longer used for resize (now uses local state)
  onOptimisticPositionClear,
  onMouseEnter,
  onMouseLeave,
  // Employee management
  employees,
  onAssignEmployee,
  onEmployeeUpdate,
  // Additional context
  businessId,
  selectedDate,
  // Feature flags
  isDraggableInPanel = false,
  showMenu = false,
  isHistorical,
  calendarVariant = 'full',
  // Data
  breaks,
  actualArrivalTime,
  actualDepartureTime,
  allDayShifts = [],
  scrollContainerRef,
  // Shift selection for template editing
  onShiftSelect,
  selectedShiftIds,
  actionsPanelMode,
  // Actions panel control for standalone functionality
  onOpenActionsPanel,
  onSetActionsPanelMode,
  // Drag handle control
  isActionsPanelOpen = false,
}) => {
  // Optimistic position state for immediate drag feedback (used by drag-and-drop)
  const [optimisticPosition, setOptimisticPosition] = useState<{
    left: number;
    width: number;
    top: number;
  } | null>(null);

  // Track when shift was just dropped for smooth landing animation
  const [justDropped, setJustDropped] = useState(false);

  // Local times for smooth resize cursor tracking (same pattern as ShiftCreationOverlay)
  const [resizeStartTime, setResizeStartTime] = useState<Date | null>(null);
  const [resizeEndTime, setResizeEndTime] = useState<Date | null>(null);

  // Memoized position calculation - only recalculates when actual values change
  const calculatedPosition = useMemo(() => {
    // Use explicit position props if available
    if (position) {
      return {
        left: position.left,
        width: position.width,
        top: position.top || 0
      };
    }

    if (propLeft !== undefined && propWidth !== undefined) {
      return {
        left: propLeft,
        width: propWidth,
        top: propTop || 0
      };
    }

    // Validate hourCellWidthPx before calculating - must be a valid measurement
    // This ensures we only calculate positions when the container has been properly measured
    if (!hourCellWidthPx || hourCellWidthPx <= 0) {
      // Container not yet measured - return zero position to signal not ready
      return { left: 0, width: 0, top: 0 };
    }

    // Calculate position when grid is ready and measurements are valid
    if (displayStartTime && displayEndTime && totalDisplayHours) {
      // Use resize times during active resize for smooth cursor tracking
      // This follows the same pattern as ShiftCreationOverlay
      const effectiveShift = (resizeStartTime && resizeEndTime)
        ? { ...shift, startTime: resizeStartTime, endTime: resizeEndTime }
        : shift;

      const calculatedPositionResult = enhancedShiftPositioningService.calculateShiftPosition(
        effectiveShift,
        displayStartTime,
        displayEndTime,
        totalDisplayHours,
        hourCellWidthPx,
        hideDayColumn || false
      );

      // Day-centered positioning: top position is relative to the day (not the month)
      const shiftRowOffset = (rowIndex || 0) * ROW_HEIGHT_PX;
      const finalTop = shiftRowOffset + 4; // 4px padding from row top

      const finalPosition = {
        left: calculatedPositionResult?.left ?? 0,
        width: calculatedPositionResult?.width ?? 100,
        top: finalTop
      };

      return finalPosition;
    }

    // Default fallback position when display times not available
    return { left: 0, width: 0, top: 0 };
  }, [
    hourCellWidthPx,
    totalDisplayHours,
    hideDayColumn,
    displayStartTime?.getTime(), // Use timestamp to prevent Date reference changes
    displayEndTime?.getTime(),   // Use timestamp to prevent Date reference changes
    rowIndex,
    shift.startTime?.getTime(),  // Use timestamp to prevent Date reference changes
    shift.endTime?.getTime(),    // Use timestamp to prevent Date reference changes
    shift.id,
    position,
    propLeft,
    propWidth,
    propTop,
    resizeStartTime?.getTime(),  // Include resize times in dependencies
    resizeEndTime?.getTime()     // Include resize times in dependencies
  ]);

  const { left, width, top } = optimisticPosition || calculatedPosition;

  // Guard: Don't render shift with zero/invalid dimensions
  // This prevents caching invalid positions in react-window and avoids layout issues
  const isPositionReady = width > 0 && (propLeft !== undefined || propWidth !== undefined || hourCellWidthPx > 0);

  const clearOptimisticPosition = useCallback(() => {
    setOptimisticPosition(null);
    if (onOptimisticPositionClear) {
      onOptimisticPositionClear(shift.id);
    }
  }, [onOptimisticPositionClear, shift.id]);

  // Clear optimistic position when shift changes or component unmounts
  useEffect(() => {
    return () => {
      clearOptimisticPosition();
    };
  }, [shift.id, clearOptimisticPosition]);

  // Clear optimistic position when shift times change (successful reposition)
  useEffect(() => {
    if (optimisticPosition) {
      clearOptimisticPosition();
    }
  }, [shift.startTime, shift.endTime]);

  // Check if this shift was recently dropped (marked by GridDropOverlay)
  // and trigger the landing animation
  useEffect(() => {
    if (isShiftRecentlyDropped(shift.id)) {
      setJustDropped(true);
      // Clear animation state after animation completes
      const timer = setTimeout(() => {
        setJustDropped(false);
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [shift.id, shift.startTime, shift.endTime]);

  // Unified employee assignment function that supports both APIs
  const handleEmployeeAssignment = useCallback(async (shiftId: string, employeeId: string | null) => {
    if (onAssignEmployee) {
      return await onAssignEmployee(shiftId, employeeId);
    } else if (onEmployeeUpdate) {
      return await onEmployeeUpdate(shiftId, employeeId);
    } else {
      throw new Error('No employee assignment handler provided');
    }
  }, [onAssignEmployee, onEmployeeUpdate]);

  // Check if employee assignment is available
  const canAssignEmployee = !!(onAssignEmployee || onEmployeeUpdate);

  // Integrate with dimming context for hover effects and select dimming
  const {
    getShiftDimmingClass,
    handleShiftHover,
    handleSelectOpen,
    handleSelectClose,
    shiftWithOpenSelect,
    hoveredShiftId,
    draggingShiftId,
    setDraggingShiftId
  } = useDimming();

  // Check if THIS shift is being dragged (survives virtualization)
  const isThisShiftBeingDragged = draggingShiftId === shift.id;

  // Integrate with unified drag & drop context for employee dragging visual feedback
  const {
    isDragging: isAnyEmployeeDragging,
    draggedEmployee: globalDraggedEmployee,
    dragContext
  } = useEmployeeDragDrop();

  // Quick copy / placing mode and resize mode from UI context
  const { startPlacingShift, resizingShiftId, enterResizeMode, exitResizeMode } = useShiftUI();

  // Check if this shift is in resize mode
  const isResizeMode = resizingShiftId === shift.id;

  // Use the robust resize hook - same pattern as ShiftCreationOverlay for smooth cursor tracking
  const { startResize } = useShiftResize({
    startTime: shift.startTime,
    endTime: shift.endTime,
    intervalWidthPx: hourCellWidthPx || 60, // Fallback to 60 if not set
    onResize: (newStart, newEnd) => {
      // Direct local state update for smooth cursor tracking
      // This triggers re-render with new position calculated via enhancedShiftPositioningService
      setResizeStartTime(newStart);
      setResizeEndTime(newEnd);
    },
    onResizeEnd: (newStart, newEnd) => {
      // Clear local resize state first
      setResizeStartTime(null);
      setResizeEndTime(null);
      // Commit the change
      if (onReposition) {
        const hasChanged = newStart.getTime() !== shift.startTime.getTime() ||
                           newEnd.getTime() !== shift.endTime.getTime();
        if (hasChanged) {
          onReposition(shift.id, newStart, newEnd);
        }
      }
    },
  });

  // Highlighted item from AI chat navigation
  const { highlightedItem } = useDashboardTab();
  const isAIHighlighted = highlightedItem?.type === 'shift' && highlightedItem?.id === shift.id;
  const highlightedShiftRef = useRef<HTMLDivElement>(null);

  // Scroll to highlighted shift when it changes
  useEffect(() => {
    if (isAIHighlighted && highlightedShiftRef.current) {
      highlightedShiftRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isAIHighlighted]);

  // Reference for click-outside detection in resize mode
  const shiftCardRef = useRef<HTMLDivElement>(null);

  // Exit resize mode when clicking outside the shift
  useEffect(() => {
    if (!isResizeMode) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (shiftCardRef.current && !shiftCardRef.current.contains(e.target as Node)) {
        exitResizeMode();
      }
    };

    // Delay adding the listener to avoid immediate trigger from the resize button click
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isResizeMode, exitResizeMode]);

  // Historical edit context for protecting past shifts
  const {
    isShiftLocked,
    wasShiftHistoricallyEdited,
    isHistoricalEditEnabled
  } = useHistoricalEdit();

  // Determine if this shift is locked based on date, historical status, OR view mode
  const isShiftProtected = useMemo(() => {
    // Lock shifts when in View mode (panel closed)
    if (!isActionsPanelOpen) {
      return true;
    }
    // Use the context's isShiftLocked function which respects historical edit mode
    return isShiftLocked(shift.startTime, shift.isHistorical || false);
  }, [isShiftLocked, shift.startTime, shift.isHistorical, isActionsPanelOpen]);

  // Check if shift was historically edited (for warning indicator)
  const showHistoricalEditWarning = useMemo(() => {
    return shift.wasHistoricallyEdited || wasShiftHistoricallyEdited(shift.id);
  }, [shift.wasHistoricallyEdited, wasShiftHistoricallyEdited, shift.id]);


  const shiftStatus = useMemo(() => {
    // Override with PLANNING status for planning preview shifts
    if (shift.isPlanningPreview) {
      return ShiftStatus.PLANNING;
    }

    return shift.calculatedStatus || ShiftStatus.PENDING;
  }, [shift.isPlanningPreview, shift.calculatedStatus]);


  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'error' | null>(null);
  const [debouncedAvailability, setDebouncedAvailability] = useState<boolean>(true);
  const [isDragZoneMenuOpen, setIsDragZoneMenuOpen] = useState(false);

  const saveStatusTimeoutRef = useRef<NodeJS.Timeout | null>(null);


  const { isEmployeeAvailable } = useEmployeeAvailability({
    employees,
    proposedStartTime: shift.startTime,
    proposedEndTime: shift.endTime,
    existingShifts: allDayShifts,
    excludeShiftId: shift.id
  });

  const activeDraggedEmployee = globalDraggedEmployee;
  
  useEffect(() => {
    if (!activeDraggedEmployee || !isAnyEmployeeDragging || dragContext !== 'shift-assignment') {
      setDebouncedAvailability(true);
      return;
    }

    try {
      const available = isEmployeeAvailable(activeDraggedEmployee.id);
      setDebouncedAvailability(available);
    } catch (error) {
      setDebouncedAvailability(false);
    }
  }, [activeDraggedEmployee, isAnyEmployeeDragging, dragContext, isEmployeeAvailable]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveStatusTimeoutRef.current) {
        clearTimeout(saveStatusTimeoutRef.current);
        saveStatusTimeoutRef.current = null;
      }
      // Clear hover state on unmount to prevent stuck states
      handleShiftHover(null, calendarVariant);
    };
  }, [handleShiftHover, calendarVariant]);

  // Standard drag configuration for moving shifts
  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: 'PLACED_SHIFT',
    item: () => {
      return {
        shiftId: shift.id,
        title: shift.title || 'Untitled',
        startTime: shift.startTime,
        endTime: shift.endTime,
        duration: differenceInMinutes(shift.endTime, shift.startTime),
        isDuplicateMode: false, // Always false - no more duplicate mode
        // Include all necessary data for moving
        employeeId: shift.employeeId,
        employeeName: shift.employeeName,
        business_id: shift.business_id,
        breaks: shift.breaks,
        // Include pixel width for accurate drag preview
        pixelWidth: width
      };
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
    canDrag: !isShiftProtected // Use the context-aware protection check
  }), [shift.id, shift.startTime, shift.endTime, shift.title, shift.employeeId, shift.employeeName, shift.business_id, shift.breaks, isShiftProtected, width]);

  // Suppress default browser drag preview - use custom VirtualDragLayer instead
  // Use useLayoutEffect to ensure preview is set synchronously before paint
  useLayoutEffect(() => {
    preview(getEmptyImage());
  }, [preview]);

  // Update dimming context when drag starts/ends
  useEffect(() => {
    if (isDragging) {
      setDraggingShiftId(shift.id);
      // Clear hover state when drag starts (DimmingContext also does this, but be explicit)
      handleShiftHover(null, calendarVariant);
      // Close drag zone menu when dragging starts
      setIsDragZoneMenuOpen(false);
    } else {
      setDraggingShiftId(null);
      // Clear hover state when drag ends to prevent stuck states
      handleShiftHover(null, calendarVariant);
    }
  }, [isDragging, shift.id, setDraggingShiftId, handleShiftHover, calendarVariant]);

  // Fix container overflow during drag operations
  useEffect(() => {
    const calendarGrid = document.querySelector('.shift-calendar-grid');
    if (calendarGrid) {
      if (isDragging) {
        // Temporarily allow overflow during drag
        calendarGrid.style.overflowX = 'visible';
        calendarGrid.style.overflowY = 'visible';
      } else {
        // Restore original overflow
        calendarGrid.style.overflowX = 'hidden';
        calendarGrid.style.overflowY = 'visible';
      }
    }

    return () => {
      // Cleanup: restore original overflow
      if (calendarGrid) {
        calendarGrid.style.overflowX = 'hidden';
        calendarGrid.style.overflowY = 'visible';
      }
    };
  }, [isDragging]);

  const dragHandleRef = useRef<HTMLDivElement>(null);

  const [{ isOver: isEmployeeOver, canDrop: canDropEmployee }, dropEmployee] = useDrop(() => ({
    accept: 'EMPLOYEE',
    drop: async (item: { employeeId: string; employeeName: string }) => {
      if (!isShiftProtected) {
        setSaveStatus('saving');

        try {
          await handleEmployeeAssignment(shift.id, item.employeeId);
          setSaveStatus('saved');

          if (saveStatusTimeoutRef.current) clearTimeout(saveStatusTimeoutRef.current);
          saveStatusTimeoutRef.current = setTimeout(() => setSaveStatus(null), 2000);

          toast.success(`${item.employeeName} assigned to shift`);
        } catch (error) {
          setSaveStatus('error');

          if (saveStatusTimeoutRef.current) clearTimeout(saveStatusTimeoutRef.current);
          saveStatusTimeoutRef.current = setTimeout(() => setSaveStatus(null), 3000);

          toast.error('Failed to assign employee');
        }
      }
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop() && !isShiftProtected && !shift.isPlanningPreview,
    }),
  }), [shift.id, isShiftProtected, shift.isPlanningPreview, handleEmployeeAssignment]);

  // Get styling from unified system
  const statusStyles = useMemo(() => {
    return getShiftStatusStyles(shiftStatus, StyleVariant.CALENDAR);
  }, [shiftStatus]);
  
  // Get dimming class from context, passing the calculated status
  // Memoize dimming class to prevent recalculation on every render
  const dimmingClass = useMemo(() => {
    return getShiftDimmingClass(shift.id, shift.employeeId, calendarVariant, shiftStatus.toString());
  }, [getShiftDimmingClass, shift.id, shift.employeeId, calendarVariant, shiftStatus]);
  
  // Memoize background color calculation to prevent unnecessary recalculations
  const backgroundColor = useMemo(() => {
    let bgColor = statusStyles.background || 'bg-blue-600'; // Ensure fallback background

    // Override background color when any employee is being dragged for visual validation
    // Only apply during shift-assignment context to avoid conflicts with other drag types
    if (isAnyEmployeeDragging && activeDraggedEmployee && dragContext === 'shift-assignment') {
      // Check if the dragged employee is already assigned to this shift
      if (shift.employeeId === activeDraggedEmployee.id) {
        // Yellow for currently assigned employee being dragged (neutral state)
        bgColor = 'bg-yellow-400/70 dark:bg-yellow-500/70 border-yellow-300 dark:border-yellow-400';
      } else {
        // Green for available, red for conflicts, with enhanced border for better visibility
        bgColor = debouncedAvailability
          ? 'bg-green-400/70 dark:bg-green-500/70 border-green-300 dark:border-green-400' // Available - green
          : 'bg-red-400/70 dark:bg-red-500/70 border-red-300 dark:border-red-400';      // Conflict - red
      }
    }

    return bgColor;
  }, [isDragging, statusStyles.background, isAnyEmployeeDragging, activeDraggedEmployee, dragContext, shift.employeeId, debouncedAvailability]);
  

  // Calculate shift duration in minutes (used for break calculations)
  const shiftDurationMinutes = differenceInMinutes(shift.endTime, shift.startTime);

  // Memoize break positions to prevent recalculation on every render
  const breakPositions = useMemo(() => {
    if (!Array.isArray(breaks) || breaks.length === 0) return [];

    const totalShiftMinutes = differenceInMinutes(shift.endTime, shift.startTime);
    if (totalShiftMinutes <= 0) return [];

    return breaks.map((breakItem, index) => {
      // Input validation
      if (!breakItem || typeof breakItem.startMinute !== 'number' || typeof breakItem.durationMinutes !== 'number') {
        return { leftPercent: 0, widthPercent: 0, key: index };
      }

      // Calculate percentage-based positioning
      const leftPercent = Math.max(0, Math.min(100, (breakItem.startMinute / totalShiftMinutes) * 100));
      const widthPercent = Math.max(0, Math.min(100 - leftPercent, (breakItem.durationMinutes / totalShiftMinutes) * 100));

      return {
        leftPercent,
        widthPercent,
        key: index,
      };
    });
  }, [breaks, shift.startTime, shift.endTime]);

  // Check if shift is selected
  const isShiftSelected = selectedShiftIds?.has(shift.id) || false;

  // Memoize status badge visibility check
  const shouldShowStatusBadge = useMemo(() => {
    return shiftStatus === ShiftStatus.PLANNING ||
           shiftStatus === ShiftStatus.PENDING ||
           shiftStatus === ShiftStatus.IN_PROGRESS ||
           shiftStatus === ShiftStatus.UNASSIGNED ||
           shiftStatus === ShiftStatus.MISSED ||
           shiftStatus === ShiftStatus.LATE ||
           shiftStatus === ShiftStatus.TOO_EARLY ||
           shiftStatus === ShiftStatus.LEFT_EARLY ||
           shiftStatus === ShiftStatus.COMPLETED_LATE ||
           shiftStatus === ShiftStatus.ON_TIME ||
           shiftStatus === ShiftStatus.OVERTIME ||
           shiftStatus === ShiftStatus.NO_DEPARTURE_SCAN;
  }, [shiftStatus]);

  // Memoize shift element classes to prevent unnecessary recalculations
  const shiftElementClasses = useMemo(() => {
    const baseClasses = [
      'h-[58px]', // Constant height
      'transition-[background-color,box-shadow,ring-color,opacity,transform]', // Smooth transitions (excluding width/left to prevent flicker)
      'duration-200', // Transition duration
      'overflow-hidden', // Prevent content overflow
      backgroundColor, // Background color from status/drag state
      // Remove border-radius for very small shifts (< 20px)
      width < 20 ? 'rounded-none' : 'rounded-md',
      shift.isNew ? 'ring-2 ring-blue-400 ring-offset-1 animate-pulse' : '',
      // AI chat navigation highlight (green ring with pulse)
      isAIHighlighted ? 'ring-2 ring-green-500 ring-offset-1 animate-pulse shadow-lg shadow-green-500/30' : '',
      dimmingClass, // Dimming effect
      (onClick || onShiftSelect) ? 'cursor-pointer hover:shadow-md' : 'cursor-default',
    ];

    // Add selection styling - similar to day selection and EditableShiftCard
    if (isShiftSelected && !isResizeMode) {
      baseClasses.push(`ring-2 ${EDITING_COLORS.RING_SOLID} ${EDITING_COLORS.BG_SELECTED} shadow-lg`);
    }

    // Resize mode - match inline creation tool border style
    if (isResizeMode) {
      baseClasses.push('outline', 'outline-2', 'outline-primary', 'shadow-lg');
    }

    // Add state-specific classes (z-index will be handled via inline CSS)
    // Use isThisShiftBeingDragged (from context) instead of isDragging (local)
    // so styling survives virtualization when scrolling
    if (isThisShiftBeingDragged) {
      // Source shift indicator - show where the shift is being dragged FROM
      baseClasses.push(
        'opacity-50',
        'outline',
        'outline-2',
        'outline-primary/70',
        EDITING_COLORS.BG_LIGHT
      );
    } else if (justDropped) {
      // Smooth landing animation after drop
      baseClasses.push('animate-shift-land');
    } else if (isEmployeeOver && canDropEmployee) {
      baseClasses.push(`ring-2 ring-blue-400 bg-blue-50/80 dark:bg-blue-900/30`);
    } else if (isAnyEmployeeDragging && activeDraggedEmployee && dragContext === 'shift-assignment') {
      baseClasses.push(`bg-gray-50/80 dark:bg-gray-800/80 shadow-md`);
    }

    return baseClasses.filter(Boolean).join(' ');
  }, [isThisShiftBeingDragged, justDropped, isEmployeeOver, canDropEmployee, isAnyEmployeeDragging, activeDraggedEmployee, dragContext, backgroundColor, width, shift.isNew, isAIHighlighted, dimmingClass, onClick, onShiftSelect, isShiftSelected, isResizeMode]);

  // Memoized event handlers for performance
  const handleStopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleResizeClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isShiftProtected) {
      toast.error('Cannot resize protected shift', {
        description: 'Enable "Editing Past" mode in the calendar header to modify this shift.'
      });
      return;
    }
    enterResizeMode(shift.id);
  }, [enterResizeMode, shift.id, isShiftProtected]);

  const handleDeleteClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isShiftProtected) {
      toast.error('Cannot delete protected shift', {
        description: 'Enable "Editing Past" mode in the calendar header to delete this shift.'
      });
      return;
    }
    if (onDelete) {
      onDelete(shift.id);
    }
  }, [onDelete, shift.id, isShiftProtected]);

  const handleSaveAsTemplate = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDragZoneMenuOpen(false);

    // Select this shift and open actions panel in template mode
    if (onShiftSelect) {
      onShiftSelect(shift.id, true);
    }
    if (onOpenActionsPanel && onSetActionsPanelMode) {
      onSetActionsPanelMode('template');
      onOpenActionsPanel();
    } else {
      toast.success('Shift selected for template', {
        description: 'Use the actions panel to save as template.'
      });
    }
  }, [shift.id, onShiftSelect, onOpenActionsPanel, onSetActionsPanelMode]);

  // Handle double-click on drag zone to start quick copy mode
  const handleDragZoneDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    startPlacingShift(shift, width);
  }, [startPlacingShift, shift, width]);

  // Drop ref only - drag preview suppression handled via getEmptyImage() above
  // Also attach highlighted shift ref for scroll-into-view and resize click-outside detection
  const combinedRef = useCallback((node: HTMLDivElement | null) => {
    dropEmployee(node);
    shiftCardRef.current = node; // For resize click-outside detection
    if (isAIHighlighted) {
      highlightedShiftRef.current = node;
    }
  }, [dropEmployee, isAIHighlighted]);

  // Drag handle - this is where drag initiates from
  const attachDragHandle = useCallback((node: HTMLDivElement | null) => {
    dragHandleRef.current = node;
    drag(node);
  }, [drag]);





  // Helper function to determine if tooltips should be disabled for this calendar variant
  const shouldDisableTooltip = calendarVariant === 'insights' || calendarVariant === 'timetracking';

  // Early return: Render placeholder when position isn't ready yet
  // This prevents zero-position elements from being cached and causing layout issues
  if (!isPositionReady) {
    return (
      <div
        className="shift-placeholder animate-pulse bg-muted/30 rounded-md"
        style={{
          position: 'absolute',
          left: 0,
          top: `${top}px`,
          width: '100px',
          height: `${ROW_HEIGHT_PX - 8}px`,
          opacity: 0.5,
        }}
      />
    );
  }

  return (
    <ShiftTooltip
      shift={shift}
      employees={employees}
      status={shiftStatus}
      actualArrivalTime={actualArrivalTime}
      actualDepartureTime={actualDepartureTime}
      delayDuration={1000}
      side="bottom"
      disabled={isDragging || shouldDisableTooltip}
      scrollContainerRef={scrollContainerRef}
      isProtected={isShiftProtected}
    >
      <div
        ref={combinedRef}
        className={shiftElementClasses}
        data-shift-id={shift.id} // For grid position restoration
        data-dragging={isDragging}
        data-tutorial="shift-card"
        style={{
          position: 'absolute',
          left: `${left}px`,
          width: `${width}px`,
          top: `${top}px`,
          pointerEvents: isDragging ? 'none' : 'auto', // Allow hover passthrough during drag
          // Direct CSS z-index based on state for better drag preview support
          zIndex: isDragging ? getZIndex('SHIFT_DRAGGING') : (isEmployeeOver && canDropEmployee ? getZIndex('DROP_ZONE') : getZIndex('SHIFT_NORMAL')),
        }}
        onClick={(e) => {
          const target = e.target as HTMLElement;

          // Check for drag zone and interactive elements that should not trigger shift selection
          const isDragZone = (
            target.closest('.drag-zone') ||
            target.closest('.drag-handle') ||
            target.closest('[data-drag-zone]') ||
            target.hasAttribute('data-stop-propagation')
          );

          const isInteractiveElement = (
            isDragZone ||
            target.closest('[data-radix-collection-item]') ||
            target.closest('select') ||
            target.closest('button') ||
            target.closest('[data-radix-menu-trigger]') ||
            target.closest('[data-radix-menu-content]')
          );

          if (isDragZone) {
            // Drag zone: Don't prevent default - let react-dnd handle drag operations
            return;
          } else if (!isInteractiveElement) {
            // Selection zone: trigger shift selection if available
            if (onShiftSelect) {
              e.preventDefault();
              e.stopPropagation();
              onShiftSelect(shift.id, !isShiftSelected);
            }
            // Fallback to original onClick handler
            else if (onClick) {
              onClick(e);
            }
          }
        }}
        onMouseEnter={() => {
          handleShiftHover(shift.id, calendarVariant);
          onMouseEnter?.();
        }}
        onMouseLeave={() => {
          handleShiftHover(null, calendarVariant);
          onMouseLeave?.();
        }}
      >
        {/* Drag Zone - Hidden when in resize mode */}
        {!isResizeMode && (
          <ShiftZoneControl
            isProtected={isShiftProtected}
            width={width}
            dragHandleRef={attachDragHandle}
            isDragZoneMenuOpen={isDragZoneMenuOpen}
            onOpenChange={setIsDragZoneMenuOpen}
            onResize={handleResizeClick}
            onDelete={handleDeleteClick}
            onSaveAsTemplate={handleSaveAsTemplate}
            onDoubleClick={handleDragZoneDoubleClick}
            canDelete={!!onDelete}
          />
        )}

        {/* Resize Handles - Only visible in resize mode */}
        {isResizeMode && !isShiftProtected && (
          <>
            {/* Left resize handle */}
            <div
              className="absolute left-0 top-0 h-full w-3 cursor-ew-resize z-30 flex items-center justify-center transition-colors bg-primary/20 hover:bg-primary/40 active:bg-primary/50"
              style={{ pointerEvents: 'auto' }}
              onPointerDown={(e) => startResize(e, 'left')}
            >
              <GripVertical className="h-4 w-4 text-primary" />
            </div>

            {/* Right resize handle */}
            <div
              className="absolute right-0 top-0 h-full w-3 cursor-ew-resize z-30 flex items-center justify-center transition-colors bg-primary/20 hover:bg-primary/40 active:bg-primary/50"
              style={{ pointerEvents: 'auto' }}
              onPointerDown={(e) => startResize(e, 'right')}
            >
              <GripVertical className="h-4 w-4 text-primary" />
            </div>
          </>
        )}

        {/* Historical Edit Warning Indicator - TooltipProvider hoisted to ComposedShiftProvider */}
        {showHistoricalEditWarning && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="absolute right-1 top-1 z-30"
                style={{ pointerEvents: 'auto' }}
              >
                <div className="p-0.5 bg-orange-500/90 rounded-sm">
                  <AlertTriangle className="h-3 w-3 text-white" />
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="font-medium">Historically Edited</p>
              <p className="text-xs text-muted-foreground">
                This shift was modified after its date
                {shift.historicalEditCount && shift.historicalEditCount > 1 && (
                  <span> ({shift.historicalEditCount} times)</span>
                )}
              </p>
            </TooltipContent>
          </Tooltip>
        )}
      {/* Content - Original vertical layout with width-based visibility */}
      {width >= 25 && (
        <div
          className="flex items-center h-full w-full pl-4 pr-1 overflow-hidden"
          style={{ position: 'relative' }}
          onClick={(e) => {
            if (!e.defaultPrevented) {
              const target = e.target as HTMLElement;
              const isDragZone = (
                target.closest('.drag-zone') ||
                target.closest('.drag-handle') ||
                target.closest('[data-drag-zone]') ||
                target.hasAttribute('data-stop-propagation')
              );
              const isBackgroundClick = (
                !isDragZone &&
                target.tagName === 'DIV' &&
                !target.closest('[data-radix-collection-item]') &&
                !target.closest('select') &&
                !target.closest('button')
              );
              if (isBackgroundClick) {
                if (onShiftSelect) {
                  e.preventDefault();
                  e.stopPropagation();
                  onShiftSelect(shift.id, !isShiftSelected);
                } else if (onClick) {
                  e.preventDefault();
                  onClick(e);
                }
              }
            }
          }}
        >
          {/* Menu */}
          {showMenu && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div
                  className="mr-1 cursor-pointer hover:bg-white/10 rounded p-1 transition-colors"
                  style={{ pointerEvents: 'auto' }}
                  data-stop-propagation="true"
                  data-radix-menu-trigger="true"
                  onClick={handleStopPropagation}
                >
                  <MoreVertical className="h-4 w-4 text-white" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem
                  onClick={handleResizeClick}
                  className="flex items-center gap-2"
                  disabled={isShiftProtected}
                >
                  <MoveHorizontal className="h-4 w-4" />
                  {isShiftProtected ? 'Resize Shift (Locked)' : 'Resize Shift'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleDeleteClick}
                  className="flex items-center gap-2 text-red-600 focus:text-red-600"
                  disabled={!onDelete || isShiftProtected}
                >
                  <Trash2 className="h-4 w-4" />
                  {isShiftProtected ? 'Delete Shift (Locked)' : 'Delete Shift'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Horizontal layout: Title on left, Status+Employee stacked on right */}
          <div className="flex flex-row items-stretch justify-between w-full h-full pointer-events-none overflow-hidden gap-1">
            {/* Left side: Title and time info */}
            {width >= 100 && (
              <div
                className={`flex flex-col justify-between h-full py-2 text-white cursor-pointer pointer-events-auto min-w-0 flex-1 ${
                  width < 120 ? 'text-[8px]' : width < 150 ? 'text-xxs' : 'text-xs'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onShiftSelect) {
                    onShiftSelect(shift.id, !isShiftSelected);
                  } else if (onClick) {
                    onClick(e);
                  }
                }}
              >
                <span className="block truncate font-medium leading-none">{shift.title || 'Untitled'}</span>
                <span className={`block leading-none ${
                  width < 120 ? 'text-[6px]' : width < 150 ? 'text-[7px]' : 'text-[8px]'
                }`}>
                  <span className="block truncate leading-none pb-1">{Math.floor(shiftDurationMinutes / 60)}h{shiftDurationMinutes % 60 > 0 ? ` ${shiftDurationMinutes % 60}m` : ''}</span>
                  <span className="block truncate leading-none">{format(shift.startTime, 'HH:mm')}-{format(shift.endTime, 'HH:mm')}</span>
                </span>
              </div>
            )}

            {/* Right side: Status badge + Employee select stacked vertically */}
            <div className={`flex flex-col items-stretch flex-shrink-0 py-1 ${
              width < 60 ? 'gap-0 w-6' : width < 80 ? 'gap-0.5 w-12' : width < 100 ? 'gap-0.5 w-16' : width < 150 ? 'gap-0.5 w-20' : 'gap-0.5 w-24'
            }`}>
              {/* Status Badge - Priority 3 */}
              {shouldShowStatusBadge && width >= 60 && (
                <ShiftStatusBadge
                  status={shiftStatus}
                  variant={StyleVariant.COMPACT}
                  showText={width >= 150}
                  className="w-full"
                  data-tutorial="shift-status-badge"
                />
              )}

              {/* Employee select/indicator - Priority 2 */}
              <EmployeeIndicator
                shift={shift}
                width={width}
                isProtected={isShiftProtected}
                canAssign={canAssignEmployee}
                businessId={shift.business_id || employees[0]?.business_id}
                allDayShifts={allDayShifts}
                onAssign={handleEmployeeAssignment}
                onSelectOpen={handleSelectOpen}
                onSelectClose={handleSelectClose}
                onSaveStatusChange={(status) => {
                  setSaveStatus(status);
                  if (saveStatusTimeoutRef.current) clearTimeout(saveStatusTimeoutRef.current);
                  if (status === 'saved') {
                    saveStatusTimeoutRef.current = setTimeout(() => setSaveStatus(null), 2000);
                  } else if (status === 'error') {
                    saveStatusTimeoutRef.current = setTimeout(() => setSaveStatus(null), 3000);
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}

        {/* Render Breaks - Only show for wider shifts */}
        {breakPositions.length > 0 && width >= 100 &&
          breakPositions.map(({ leftPercent, widthPercent, key }) => (
            <div
              key={key}
              className="absolute bg-red-500/70 dark:bg-red-400/70 h-full rounded-md pointer-events-none"
              style={{
                left: `${leftPercent}%`,
                width: `${widthPercent}%`,
              }}
              title="Break"
            />
          ))
        }
      </div>
    </ShiftTooltip>
  );
};

// Memoize component with custom comparison to prevent unnecessary rerenders
// This fixes the flickering hover issue where frequent parent rerenders
// interrupt the 1000ms hover delay needed for dimming and tooltips
export default React.memo(DraggableShift, (prevProps, nextProps) => {
  // Core shift data - rerender if any of these change
  if (
    prevProps.shift.id !== nextProps.shift.id ||
    prevProps.shift.startTime?.getTime() !== nextProps.shift.startTime?.getTime() ||
    prevProps.shift.endTime?.getTime() !== nextProps.shift.endTime?.getTime() ||
    prevProps.shift.employeeId !== nextProps.shift.employeeId ||
    prevProps.shift.employeeName !== nextProps.shift.employeeName ||
    prevProps.shift.title !== nextProps.shift.title ||
    prevProps.shift.calculatedStatus !== nextProps.shift.calculatedStatus ||
    prevProps.shift.isPlanningPreview !== nextProps.shift.isPlanningPreview ||
    prevProps.shift.isNew !== nextProps.shift.isNew ||
    prevProps.shift.requestCount !== nextProps.shift.requestCount ||
    prevProps.shift.business_id !== nextProps.shift.business_id ||
    prevProps.shift.source !== nextProps.shift.source ||
    prevProps.shift.wasHistoricallyEdited !== nextProps.shift.wasHistoricallyEdited ||
    prevProps.shift.historicalEditCount !== nextProps.shift.historicalEditCount
  ) {
    return false; // Props changed, rerender
  }

  // Position props - rerender if positioning changes
  if (
    prevProps.left !== nextProps.left ||
    prevProps.width !== nextProps.width ||
    prevProps.top !== nextProps.top ||
    prevProps.rowIndex !== nextProps.rowIndex ||
    prevProps.position?.left !== nextProps.position?.left ||
    prevProps.position?.width !== nextProps.position?.width ||
    prevProps.position?.top !== nextProps.position?.top ||
    prevProps.position?.height !== nextProps.position?.height
  ) {
    return false; // Props changed, rerender
  }

  // Display configuration - rerender if grid dimensions change
  if (
    prevProps.hourCellWidthPx !== nextProps.hourCellWidthPx ||
    prevProps.totalDisplayHours !== nextProps.totalDisplayHours ||
    prevProps.hideDayColumn !== nextProps.hideDayColumn ||
    prevProps.displayStartTime?.getTime() !== nextProps.displayStartTime?.getTime() ||
    prevProps.displayEndTime?.getTime() !== nextProps.displayEndTime?.getTime()
  ) {
    return false; // Props changed, rerender
  }

  // Selection state - rerender only if THIS shift's selection changed
  // Don't compare Set references, compare if THIS shift is selected
  const prevSelected = prevProps.selectedShiftIds?.has(prevProps.shift.id);
  const nextSelected = nextProps.selectedShiftIds?.has(nextProps.shift.id);
  if (prevSelected !== nextSelected) {
    return false; // THIS shift's selection changed, rerender
  }

  // Selection size changed - only rerender if this shift is selected (for visual feedback)
  if (prevSelected && prevProps.selectedShiftIds?.size !== nextProps.selectedShiftIds?.size) {
    return false; // Selection count changed and this shift is part of selection
  }

  // Actions panel state changes
  if (
    prevProps.actionsPanelMode !== nextProps.actionsPanelMode ||
    prevProps.isActionsPanelOpen !== nextProps.isActionsPanelOpen
  ) {
    return false; // Props changed, rerender
  }

  // Feature flags and variant - rerender if these change
  if (
    prevProps.showMenu !== nextProps.showMenu ||
    prevProps.isHistorical !== nextProps.isHistorical ||
    prevProps.calendarVariant !== nextProps.calendarVariant ||
    prevProps.isDraggableInPanel !== nextProps.isDraggableInPanel
  ) {
    return false; // Props changed, rerender
  }

  // Employee list - rerender if employee list changes
  // Check length first (fast check)
  if (prevProps.employees.length !== nextProps.employees.length) {
    return false; // Props changed, rerender
  }
  // If length is same, check if employee IDs changed (prevents stale data)
  // Only check first and last as heuristic for performance
  if (prevProps.employees.length > 0) {
    if (
      prevProps.employees[0]?.id !== nextProps.employees[0]?.id ||
      prevProps.employees[prevProps.employees.length - 1]?.id !==
      nextProps.employees[nextProps.employees.length - 1]?.id
    ) {
      return false; // Employee list content changed
    }
  }

  // Breaks data - rerender if breaks change
  if (prevProps.breaks !== nextProps.breaks) {
    return false; // Props changed, rerender
  }

  // Time tracking data - rerender if actual times change
  if (
    prevProps.actualArrivalTime !== nextProps.actualArrivalTime ||
    prevProps.actualDepartureTime !== nextProps.actualDepartureTime
  ) {
    return false; // Props changed, rerender
  }

  // Additional context
  if (
    prevProps.businessId !== nextProps.businessId ||
    prevProps.selectedDate?.getTime() !== nextProps.selectedDate?.getTime()
  ) {
    return false; // Props changed, rerender
  }

  // ScrollContainerRef - only rerender if the actual container element changes
  if (prevProps.scrollContainerRef?.current !== nextProps.scrollContainerRef?.current) {
    return false;
  }

  // AllDayShifts - better comparison than just length
  if (prevProps.allDayShifts?.length !== nextProps.allDayShifts?.length) {
    return false;
  }
  // If length is same, compare first and last shift IDs as a heuristic
  if (
    prevProps.allDayShifts?.length &&
    nextProps.allDayShifts?.length &&
    (prevProps.allDayShifts[0]?.id !== nextProps.allDayShifts[0]?.id ||
     prevProps.allDayShifts[prevProps.allDayShifts.length - 1]?.id !==
     nextProps.allDayShifts[nextProps.allDayShifts.length - 1]?.id)
  ) {
    return false;
  }

  // Event handlers - Don't compare function references
  // They change on every parent render but shouldn't trigger rerenders
  // Hover events (onMouseEnter/onMouseLeave) are handled internally via DimmingContext
  // Other event handlers (onClick, onEdit, onDelete, etc.) are stable enough
  // that we don't need to rerender just because the function reference changed

  // All relevant props are equal, skip rerender
  // This prevents unnecessary rerenders that interrupt hover timeouts
  return true;
});
